import { useCallback, useMemo, useState } from 'react';

import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { DatePicker, DateRangePicker } from '@mui/lab';
import { RangeInput } from '@mui/lab/DateRangePicker/RangeTypes';
import {
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  FilterOptionsState,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputAdornment,
  Chip,
  Divider,
  ListItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Box } from '@mui/system';
import { parse } from 'date-fns';
import { matchSorter } from 'match-sorter';

import { InputClearButton } from 'components';
import { OrganizationAutocomplete } from 'components/OrganizationAutocomplete';
import { settings } from 'config';
import {
  allFlagOptions,
  flagOptions,
  handleDerogOptions,
  safeFormatDate,
  sivStatusToProcess,
  sivStatusIdOptions,
} from 'helpers';
import { useCandidateAdminContext } from 'hooks';
import {
  OrganizationProps,
  FlagType,
  OptionalCallback,
  FlaggedForReviewFlag,
} from 'types';
import {
  AuditorCandidateTableGlobalFilterOptions,
  ContractOwner,
  ReviewStatus,
  SivStatus,
} from 'types/Candidate';
import { Option } from 'types/Form';

import { SidebarWrapper } from './styledComponents';

interface SidebarProps {
  setFilters: (
    arg: OptionalCallback<AuditorCandidateTableGlobalFilterOptions>,
  ) => void;
  filters: AuditorCandidateTableGlobalFilterOptions;
}

const sortFunction = (a: Option, b: Option) => {
  if (a.subLabel && b.subLabel) {
    return (b.value as number) - (a.value as number);
  }
  if (a.subLabel) {
    return -1;
  }
  if (b.subLabel) {
    return 1;
  }
  return (b.value as number) - (a.value as number);
};

export const AuditorCandidateTableSidebar: React.FC<SidebarProps> = ({
  setFilters,
  filters,
}) => {
  const {
    birthdate,
    case_number,
    contract_owner_only,
    firstname,
    flag_types_exclude,
    flag_types_include,
    has_derogs,
    has_for_cause,
    last_reviewed_date_max,
    last_reviewed_date_min,
    lastname,
    organization_id,
    review_status_includes,
    tazkera,
    tranche_id,
    validated_work_days_max,
    validated_work_days_min,
    supervisor_given_name,
    supervisor_surname,
    supervisor_email,
    employer_email,
    employer_occupation,
    include_siv_ineligible,
    siv_status_id,
    passport_number,
  } = filters ?? {};
  const { DATE_FORMAT } = settings;

  const [expanded, setExpanded] = useState(true);
  const { organizationArray, trancheOptionArray } = useCandidateAdminContext();

  // used to track the flag dropdown so we can capture chip deletes
  const [flaggedSelectOpen, setFlaggedSelectOpen] = useState(false);

  // Add extra option for unassigned = candidates in no tranche
  const trauncheOptions = useMemo(
    () => [
      { value: 0, label: 'Unassigned', subLabel: '' },
      ...trancheOptionArray.sort(sortFunction),
    ],
    [trancheOptionArray],
  );
  const traunchValue = useMemo(() => {
    const selectedTrancheIdSet = new Set(tranche_id);
    return trauncheOptions.filter((t) =>
      selectedTrancheIdSet.has(t.value as number),
    );
  }, [tranche_id, trauncheOptions]);

  const employerValue = useMemo(() => {
    const selectedEmployerIdSet = new Set(organization_id);
    return organizationArray.filter((o) => selectedEmployerIdSet.has(o.id));
  }, [organization_id, organizationArray]);

  const reviewedByEmployerValue = useMemo(() => {
    if (
      review_status_includes?.includes('did_not_work_insufficient_info') &&
      review_status_includes?.includes('did_not_work_certain') &&
      review_status_includes?.includes('reviewed_and_verified')
    ) {
      return 'yes';
    }
    if (review_status_includes?.includes('pending_user_review')) {
      return 'no';
    }
    if (review_status_includes?.includes('pending_user_review')) {
      return 'review_in_progress';
    }
    return '';
  }, [review_status_includes]);

  const employmentConfirmationValue = useMemo(() => {
    if (
      review_status_includes?.length === 1 &&
      review_status_includes?.includes('reviewed_and_verified')
    ) {
      return 'yes';
    }
    if (
      review_status_includes?.length === 1 &&
      review_status_includes?.includes('did_not_work_certain')
    ) {
      return 'no';
    }
    if (
      review_status_includes?.length === 1 &&
      review_status_includes?.includes('did_not_work_insufficient_info')
    ) {
      return 'insufficient_data';
    }
    return '';
  }, [review_status_includes]);

  const durationValue = useMemo(() => {
    if (validated_work_days_min === 365) {
      return '365_or_more';
    }
    if (validated_work_days_max === 364) {
      return 'less_than_365';
    }
    return '';
  }, [validated_work_days_max, validated_work_days_min]);

  const flaggedForReviewNoFlagsSelected = useMemo(
    () => (flag_types_exclude?.length ?? 0) > 0,
    [flag_types_exclude],
  );
  const flaggedForReviewValue: Array<FlaggedForReviewFlag> = useMemo(() => {
    if (flaggedForReviewNoFlagsSelected) {
      return ['no_flags'];
    }
    return flag_types_include ?? [];
  }, [flag_types_include, flaggedForReviewNoFlagsSelected]);
  const updateFlags = useCallback(
    (flaggedForReview: Array<FlaggedForReviewFlag>) => {
      if (flaggedForReview.includes('no_flags')) {
        setFilters((prev) => ({
          ...prev,
          flag_types_include: undefined,
          flag_types_exclude: flagOptions.map((flag) => flag.value),
        }));
      } else if (flaggedForReview.includes('all_flags')) {
        setFilters((prev) => ({
          ...prev,
          flag_types_include: flagOptions.map((flag) => flag.value),
          flag_types_exclude: undefined,
        }));
      } else {
        setFilters((prev) => ({
          ...prev,
          flag_types_include: flaggedForReview.length
            ? (flaggedForReview as Array<FlagType>)
            : undefined,
          flag_types_exclude: undefined,
        }));
      }
    },
    [setFilters],
  );

  const derogatoryValue = useMemo(
    () =>
      has_for_cause === undefined
        ? has_derogs === undefined
          ? ''
          : has_derogs
          ? 'with_derog'
          : 'without_derog'
        : 'terminated_for_cause',
    [has_derogs, has_for_cause],
  );

  const dateReviewedByEmployerValue: RangeInput<string> = useMemo(
    () =>
      last_reviewed_date_min && last_reviewed_date_max
        ? [
            parse(last_reviewed_date_min, DATE_FORMAT, new Date()),
            parse(last_reviewed_date_max, DATE_FORMAT, new Date()),
          ]
        : [null, null],
    [last_reviewed_date_max, last_reviewed_date_min, DATE_FORMAT],
  );
  const updateSivStatus = useCallback(
    (sivStatusArray: Array<SivStatus>) => {
      setFilters((prev) => ({
        ...prev,
        siv_status_id: sivStatusArray || undefined,
        include_siv_ineligible: sivStatusArray?.some(
          (status) => !sivStatusToProcess.includes(status),
        ),
      }));
    },
    [setFilters],
  );
  return (
    <SidebarWrapper expanded={expanded}>
      <header>
        {expanded && <Typography variant='h5'>Filter</Typography>}
        <IconButton
          onClick={() => setExpanded(!expanded)}
          key='expand'
          role='button'
          aria-label='expand and close filter'
          aria-expanded
        >
          {expanded ? (
            <KeyboardArrowLeft fontSize='medium' />
          ) : (
            <KeyboardArrowRight fontSize='medium' />
          )}
        </IconButton>
      </header>
      {expanded && (
        <section>
          <FormControlLabel
            sx={{ mb: 2, ml: 0.1 }}
            control={
              <Switch
                checked={include_siv_ineligible}
                onChange={(e, checked) => {
                  setFilters((prev) => ({
                    ...prev,
                    include_siv_ineligible: checked || undefined,
                  }));
                }}
                size='small'
                color='warning'
              />
            }
            label={
              <Typography sx={{ ml: 1 }} variant='body2'>
                Include Non-Reviewable Cases
              </Typography>
            }
            labelPlacement='end'
          />
          <Autocomplete
            value={traunchValue}
            renderOption={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: Option,
            ) => {
              return (
                <ListItem {...props} key={option.value}>
                  <ListItemText
                    primary={option.label}
                    secondary={
                      <Typography
                        sx={{ display: 'inline' }}
                        component='span'
                        variant='body2'
                        color='text.primary'
                      >
                        {option.subLabel}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            }}
            isOptionEqualToValue={(opt: Option, val: Option) =>
              opt.value === val.value
            }
            options={trauncheOptions}
            fullWidth
            size='small'
            filterSelectedOptions
            multiple
            renderInput={(params) => <TextField {...params} label='Tranche' />}
            onChange={(_, option) => {
              setFilters((prev) => ({
                ...prev,
                tranche_id: option.length
                  ? option.map(({ value }) => value as number)
                  : undefined,
              }));
            }}
          />
          <FormControl fullWidth size='small'>
            <InputLabel id='siv-status-id-select-label'>SIV Status</InputLabel>
            <Select
              labelId='siv-status-id-select-label'
              value={siv_status_id || []}
              label='SIV Status'
              multiple
              onChange={({ target: { value } }) => {
                updateSivStatus(value as Array<SivStatus>);
              }}
              renderValue={(sivStatus) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {sivStatus.map((status) => (
                    <Chip key={status} label={status} size='small' />
                  ))}
                </Box>
              )}
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!siv_status_id}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      siv_status_id: undefined,
                      include_siv_ineligible: undefined,
                    }))
                  }
                />
              }
            >
              {Object.entries(sivStatusIdOptions).map(([key, option]) => (
                <MenuItem key={key} value={option.value}>
                  <Checkbox
                    checked={(siv_status_id ?? []).includes(option.value)}
                  />
                  <ListItemText
                    primary={option.value}
                    secondary={option.label}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small'>
            <InputLabel id='confirmation-select-label'>
              Employment Confirmation
            </InputLabel>
            <Select
              labelId='confirmation-select-label'
              value={employmentConfirmationValue}
              label='Employment Confirmation'
              onChange={({ target: { value: employment_confirmation } }) => {
                let review_status_includes: Array<ReviewStatus> | undefined =
                  undefined;
                switch (employment_confirmation) {
                  case 'yes':
                    review_status_includes = ['reviewed_and_verified'];
                    break;
                  case 'no':
                    review_status_includes = ['did_not_work_certain'];
                    break;
                  case 'insufficient_data':
                    review_status_includes = ['did_not_work_insufficient_info'];
                    break;
                  default:
                    break;
                }
                setFilters((prev) => ({
                  ...prev,
                  review_status_includes,
                }));
              }}
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!employmentConfirmationValue}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      review_status_includes: undefined,
                    }))
                  }
                />
              }
            >
              <MenuItem value='yes'>Yes</MenuItem>
              <MenuItem value='no'>No</MenuItem>
              <MenuItem value='insufficient_data'>Insufficient Data</MenuItem>
            </Select>
          </FormControl>
          <OrganizationAutocomplete
            value={employerValue}
            options={organizationArray}
            getOptionLabel={(org: OrganizationProps) => org.name || ''}
            disabled={organizationArray.length === 0}
            isOptionEqualToValue={(
              opt: OrganizationProps,
              val: OrganizationProps,
            ) => opt.id === val.id}
            renderOption={(props, option) => [props, option] as React.ReactNode}
            filterOptions={(
              options: Array<OrganizationProps>,
              { inputValue }: FilterOptionsState<OrganizationProps>,
            ) => matchSorter(options, inputValue, { keys: ['name'] })}
            fullWidth
            size='small'
            filterSelectedOptions
            multiple
            renderInput={(params) => (
              <TextField {...params} label='Employer Name' />
            )}
            onChange={(_, employers: Array<OrganizationProps>) => {
              setFilters((prev) => ({
                ...prev,
                organization_id: employers.length
                  ? employers.map(({ id }) => id)
                  : undefined,
              }));
            }}
          />
          <FormControl fullWidth size='small'>
            <InputLabel id='reviewed-select-label'>Status of Review</InputLabel>
            <Select
              labelId='reviewed-select-label'
              value={reviewedByEmployerValue}
              label='Status of Review'
              onChange={({ target: { value: reviewed_by_employer } }) => {
                let review_status_includes: Array<ReviewStatus> | undefined =
                  undefined;
                switch (reviewed_by_employer) {
                  case 'yes':
                    review_status_includes = [
                      'did_not_work_insufficient_info',
                      'did_not_work_certain',
                      'reviewed_and_verified',
                    ];
                    break;
                  case 'no':
                    review_status_includes = ['pending_user_review'];
                    break;
                  case 'incomplete':
                    review_status_includes = ['review_in_progress'];
                    break;
                  default:
                    break;
                }
                setFilters((prev) => ({
                  ...prev,
                  review_status_includes,
                }));
              }}
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!reviewedByEmployerValue}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      review_status_includes: undefined,
                    }))
                  }
                />
              }
            >
              <MenuItem value='yes'>Yes</MenuItem>
              <MenuItem value='no'>No</MenuItem>
              <MenuItem value='incomplete'>Incomplete</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size='small'>
            <InputLabel id='flagged-select-label'>Flags</InputLabel>
            <Select<Array<FlaggedForReviewFlag>>
              className='flagged-select'
              labelId='flagged-select-label'
              value={flaggedForReviewValue ?? []}
              label='Flags'
              multiple
              renderValue={(flags) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {flags.map((flag) => (
                    <Chip
                      key={flag}
                      label={
                        allFlagOptions?.find(
                          (flagOption) => flag === flagOption.value,
                        )?.value || ''
                      }
                      size='small'
                      onDelete={() =>
                        updateFlags(
                          (flaggedForReviewValue ?? []).filter(
                            (f) => f !== flag,
                          ),
                        )
                      }
                    />
                  ))}
                </Box>
              )}
              open={flaggedSelectOpen}
              onOpen={({ target }) => {
                // don't trigger the open event when a chip is being deleted
                if (target instanceof HTMLDivElement) {
                  setFlaggedSelectOpen(true);
                }
              }}
              onClose={() => setFlaggedSelectOpen(false)}
              onChange={({ target: { value } }) =>
                updateFlags(value as Array<FlaggedForReviewFlag>)
              }
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={flaggedForReviewValue.length > 0}
                  onClick={() => updateFlags([])}
                />
              }
            >
              <MenuItem value='no_flags'>
                <Checkbox checked={(flag_types_exclude?.length ?? 0) > 0} />
                <ListItemText primary='No Flags' />
              </MenuItem>
              <Divider />
              <MenuItem value='all_flags'>
                <ListItemText primary='All Flags' />
              </MenuItem>
              {flagOptions.map((flag) => (
                <MenuItem
                  key={flag.value}
                  value={flag.value}
                  disabled={flaggedForReviewNoFlagsSelected}
                >
                  <Checkbox
                    checked={(flaggedForReviewValue ?? []).includes(
                      flag.value as FlaggedForReviewFlag,
                    )}
                  />
                  <ListItemText primary={flag.value} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small'>
            <InputLabel id='derog-select-label'>
              Derogatory Information
            </InputLabel>
            <Select
              labelId='derog-select-label'
              value={derogatoryValue}
              label='Derogatory Information'
              onChange={({ target: { value: derogatory_info } }) =>
                setFilters((prev) => ({
                  ...prev,
                  has_derogs: handleDerogOptions(derogatory_info),
                  has_for_cause:
                    derogatory_info === 'terminated_for_cause'
                      ? true
                      : undefined,
                }))
              }
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!derogatoryValue}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      has_derogs: undefined,
                      has_for_cause: undefined,
                    }))
                  }
                />
              }
            >
              <MenuItem value='with_derog'>With Derog</MenuItem>
              <MenuItem value='without_derog'>Without Derog</MenuItem>
              <MenuItem value='terminated_for_cause'>
                Terminated for Cause (includes Derog)
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size='small'>
            <InputLabel id='duration-select-label'>Duration</InputLabel>
            <Select
              labelId='duration-select-label'
              value={durationValue}
              label='Duration'
              onChange={({ target: { value: duration } }) => {
                let validated_work_days_min: number | undefined = undefined;
                let validated_work_days_max: number | undefined = undefined;
                switch (duration) {
                  case '365_or_more':
                    validated_work_days_min = 365;
                    break;
                  case 'less_than_365':
                    validated_work_days_max = 364;
                    break;
                  default:
                    break;
                }
                setFilters((prev) => ({
                  ...prev,
                  validated_work_days_min,
                  validated_work_days_max,
                }));
              }}
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!durationValue}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      validated_work_days_min: undefined,
                      validated_work_days_max: undefined,
                    }))
                  }
                />
              }
            >
              <MenuItem value='365_or_more'>365 or More</MenuItem>
              <MenuItem value='less_than_365'>Less than 365</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size='small'>
            <InputLabel id='contract-select-label'>
              DOW or Other Agency Contract
            </InputLabel>
            <Select
              labelId='contract-select-label'
              value={contract_owner_only ?? ''}
              label='DOW or Other Agency Contract'
              onChange={({ target: { value: dod_or_other_contract } }) => {
                setFilters((prev) => ({
                  ...prev,
                  contract_owner_only: dod_or_other_contract as ContractOwner,
                }));
              }}
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!contract_owner_only}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      contract_owner_only: undefined,
                    }))
                  }
                />
              }
            >
              <MenuItem value='dod_contract'>DOW Contract</MenuItem>
              <MenuItem value='not_dod_contract'>Not DOW Contract</MenuItem>
              <MenuItem value='insufficient_information'>
                Insufficient Information
              </MenuItem>
            </Select>
          </FormControl>
          <DateRangePicker
            // NOTE: startText is depricated, however label does not have the desired effect here
            startText='Date Reviewed by Employer'
            mask='__/__/____'
            inputFormat='dd/MM/yyyy'
            calendars={1}
            onChange={(last_reviewed_date) => {
              setFilters((prev) => ({
                ...prev,
                last_reviewed_date_min: last_reviewed_date[0]
                  ? safeFormatDate(last_reviewed_date[0], DATE_FORMAT)
                  : undefined,
                last_reviewed_date_max: last_reviewed_date[1]
                  ? safeFormatDate(last_reviewed_date[1], DATE_FORMAT)
                  : undefined,
              }));
            }}
            value={dateReviewedByEmployerValue}
            renderInput={(startParams /*endParams*/) => (
              <TextField
                className='date-reviewed'
                fullWidth
                size='small'
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end' disablePointerEvents>
                      <IconButton role='button' aria-label='date range picker'>
                        <DateRangeIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...startParams}
              />
            )}
            clearable
          />
          <Divider />
          <TextField
            fullWidth
            size='small'
            label='Given Name'
            value={firstname ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                firstname: value ? value : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='Family Name'
            value={lastname ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                lastname: value ? value : undefined,
              }));
            }}
          />
          <DatePicker
            label='DOB'
            mask='__/__/____'
            inputFormat='dd/MM/yyyy'
            onChange={(birthdate) => {
              setFilters((prev) => ({
                ...prev,
                birthdate: birthdate
                  ? safeFormatDate(birthdate, DATE_FORMAT)
                  : undefined,
              }));
            }}
            value={birthdate ? parse(birthdate, DATE_FORMAT, new Date()) : null}
            renderInput={(params) => (
              <TextField fullWidth size='small' {...params} />
            )}
            clearable
          />
          <TextField
            fullWidth
            size='small'
            label='Tazkera'
            value={tazkera ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                tazkera: value ? value : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='NVCSIV'
            value={case_number ?? ''}
            onChange={({ target: { value: caseNumber } }) => {
              setFilters((prev) => ({
                ...prev,
                case_number: caseNumber ? caseNumber.toUpperCase() : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='Passport'
            value={passport_number ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                passport_number: value ? value : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='Employer Email'
            value={employer_email ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                employer_email: value ? value : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='Occupation'
            value={employer_occupation ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                employer_occupation: value ? value : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='Supervisor Email'
            value={supervisor_email ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                supervisor_email: value ? value : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='Supervisor First Name'
            value={supervisor_given_name ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                supervisor_given_name: value ? value : undefined,
              }));
            }}
          />
          <TextField
            fullWidth
            size='small'
            label='Supervisor Last Name'
            value={supervisor_surname ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                supervisor_surname: value ? value : undefined,
              }));
            }}
          />
        </section>
      )}
    </SidebarWrapper>
  );
};
